import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Tabs, Tab } from 'react-bootstrap';
import { Search, Filter, RefreshCw, User, Briefcase, Eye, ToggleLeft, ToggleRight, X } from 'lucide-react';
import { getAllUsers, getUserDetails, updateUserStatus } from '../../services/adminService';

const UserManagement = () => {
  const [activeTab, setActiveTab] = useState('all');
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showUserModal, setShowUserModal] = useState(false);
  const [statusUpdateLoading, setStatusUpdateLoading] = useState(false);

  // Fetch users based on active tab
  useEffect(() => {
    const fetchUsers = async () => {
      setLoading(true);
      try {
        const response = await getAllUsers({
          type: activeTab,
          page: currentPage,
          limit: 10,
          search: searchQuery
        });
        if (response.success) {
          setUsers(response.data.users);
          setFilteredUsers(response.data.users);
          setTotalPages(response.data.pagination.totalPages);
        } else {
          setError(response.error || 'Failed to fetch users');
        }
      } catch (err) {
        setError('An error occurred while fetching users');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, [activeTab, currentPage, searchQuery]);

  // Handle tab change
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setCurrentPage(1);
    setSelectedUser(null);
  };

  // Handle search
  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };

  // Handle user selection
  const handleSelectUser = async (user) => {
    try {
      const response = await getUserDetails(user.id, user.userType);
      if (response.success) {
        setSelectedUser(response.data.user);
        setShowUserModal(true);
      } else {
        setError(response.error || 'Failed to fetch user details');
      }
    } catch (err) {
      setError('An error occurred while fetching user details');
      console.error(err);
    }
  };

  // Handle status toggle
  const handleToggleStatus = async (user) => {
    setStatusUpdateLoading(true);
    try {
      const response = await updateUserStatus(user.id, {
        type: user.userType,
        isActive: !user.isActive
      });
      if (response.success) {
        // Update user in the list
        const updatedUsers = users.map(u => 
          u.id === user.id ? { ...u, isActive: !u.isActive } : u
        );
        setUsers(updatedUsers);
        setFilteredUsers(updatedUsers);

        // Update selected user if open in modal
        if (selectedUser && selectedUser.id === user.id) {
          setSelectedUser({ ...selectedUser, isActive: !selectedUser.isActive });
        }
      } else {
        setError(response.error || 'Failed to update user status');
      }
    } catch (err) {
      setError('An error occurred while updating user status');
      console.error(err);
    } finally {
      setStatusUpdateLoading(false);
    }
  };

  // Handle pagination
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Use mock data if API call fails or during development
  const mockUsers = [
    {
      id: 1,
      name: 'Vikram Malhotra',
      email: 'vikram@techcorp.in',
      userType: 'employer',
      companyName: 'TechCorp Solutions',
      contactNumber: '+919876543210',
      isActive: true,
      createdAt: '2025-01-15T10:30:00Z',
      profileCompleted: true
    },
    {
      id: 2,
      name: 'Anaya Sharma',
      email: 'anaya@gmail.com',
      userType: 'candidate',
      skills: ['React', 'Node.js', 'MongoDB'],
      location: 'Bengaluru, India',
      isActive: true,
      createdAt: '2025-02-20T14:45:00Z',
      profileCompleted: true
    }
  ];

  // If loading, show loading state
  if (loading && users.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  // If error and no users, show error state
  if (error && users.length === 0) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative" role="alert">
        <strong className="font-bold">Error: </strong>
        <span className="block sm:inline">{error}</span>
      </div>
    );
  }

  // Use fetched users or mock data
  const displayUsers = users.length > 0 ? users : mockUsers;

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <h2 className="text-2xl font-bold text-gray-900">User Management</h2>
        <div className="flex items-center space-x-2">
          <div className="relative">
            <input
              type="text"
              placeholder="Search users..."
              value={searchQuery}
              onChange={handleSearch}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <Search className="absolute left-3 top-2.5 text-gray-400" size={18} />
          </div>
          <button 
            onClick={() => {
              setSearchQuery('');
              setCurrentPage(1);
            }}
            className="flex items-center px-4 py-2 bg-white border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            <RefreshCw size={16} className="mr-2" />
            Reset
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <Tabs
          activeKey={activeTab}
          onSelect={(k) => handleTabChange(k)}
          className="mb-3 border-b"
        >
          <Tab eventKey="all" title="All Users" className="p-4">
            <UserTable 
              users={displayUsers} 
              onSelectUser={handleSelectUser} 
              onToggleStatus={handleToggleStatus}
              statusUpdateLoading={statusUpdateLoading}
              formatDate={formatDate}
            />
          </Tab>
          <Tab eventKey="candidates" title="Candidates" className="p-4">
            <UserTable 
              users={displayUsers.filter(user => user.userType === 'candidate')} 
              onSelectUser={handleSelectUser} 
              onToggleStatus={handleToggleStatus}
              statusUpdateLoading={statusUpdateLoading}
              formatDate={formatDate}
            />
          </Tab>
          <Tab eventKey="employers" title="Employers" className="p-4">
            <UserTable 
              users={displayUsers.filter(user => user.userType === 'employer')} 
              onSelectUser={handleSelectUser} 
              onToggleStatus={handleToggleStatus}
              statusUpdateLoading={statusUpdateLoading}
              formatDate={formatDate}
            />
          </Tab>
        </Tabs>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center mt-4">
          <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium ${
                currentPage === 1 ? 'text-gray-300' : 'text-gray-500 hover:bg-gray-50'
              }`}
            >
              Previous
            </button>
            {[...Array(totalPages)].map((_, i) => (
              <button
                key={i}
                onClick={() => handlePageChange(i + 1)}
                className={`relative inline-flex items-center px-4 py-2 border ${
                  currentPage === i + 1
                    ? 'bg-blue-50 border-blue-500 text-blue-600'
                    : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                } text-sm font-medium`}
              >
                {i + 1}
              </button>
            ))}
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium ${
                currentPage === totalPages ? 'text-gray-300' : 'text-gray-500 hover:bg-gray-50'
              }`}
            >
              Next
            </button>
          </nav>
        </div>
      )}

      {/* User Detail Modal */}
      {showUserModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="flex justify-between items-center border-b p-4">
              <h3 className="text-xl font-bold text-gray-900">User Details</h3>
              <button
                onClick={() => setShowUserModal(false)}
                className="text-gray-400 hover:text-gray-500"
              >
                <X size={24} />
              </button>
            </div>
            <div className="p-6">
              <div className="flex items-center mb-6">
                <div className="bg-gray-100 rounded-full p-3 mr-4">
                  {selectedUser.userType === 'candidate' ? (
                    <User size={32} className="text-blue-500" />
                  ) : (
                    <Briefcase size={32} className="text-green-500" />
                  )}
                </div>
                <div>
                  <h4 className="text-xl font-bold text-gray-900">{selectedUser.name}</h4>
                  <p className="text-gray-500">{selectedUser.email}</p>
                </div>
                <div className="ml-auto">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    selectedUser.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}>
                    {selectedUser.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h5 className="text-sm font-medium text-gray-500 mb-2">Basic Information</h5>
                  <div className="bg-gray-50 p-4 rounded-md">
                    <p className="mb-2">
                      <span className="font-medium">User Type:</span>{' '}
                      <span className="capitalize">{selectedUser.userType}</span>
                    </p>
                    <p className="mb-2">
                      <span className="font-medium">Registered:</span>{' '}
                      {formatDate(selectedUser.createdAt)}
                    </p>
                    <p className="mb-2">
                      <span className="font-medium">Profile Status:</span>{' '}
                      {selectedUser.profileCompleted ? 'Complete' : 'Incomplete'}
                    </p>
                  </div>
                </div>

                {selectedUser.userType === 'employer' && (
                  <div>
                    <h5 className="text-sm font-medium text-gray-500 mb-2">Company Information</h5>
                    <div className="bg-gray-50 p-4 rounded-md">
                      <p className="mb-2">
                        <span className="font-medium">Company Name:</span>{' '}
                        {selectedUser.companyName}
                      </p>
                      <p className="mb-2">
                        <span className="font-medium">Contact Number:</span>{' '}
                        {selectedUser.contactNumber}
                      </p>
                      <p className="mb-2">
                        <span className="font-medium">Type:</span>{' '}
                        {selectedUser.isConsultancy ? 'Consultancy' : 'Direct Employer'}
                      </p>
                    </div>
                  </div>
                )}

                {selectedUser.userType === 'candidate' && (
                  <div>
                    <h5 className="text-sm font-medium text-gray-500 mb-2">Professional Information</h5>
                    <div className="bg-gray-50 p-4 rounded-md">
                      <p className="mb-2">
                        <span className="font-medium">Skills:</span>{' '}
                        {selectedUser.skills ? selectedUser.skills.join(', ') : 'Not specified'}
                      </p>
                      <p className="mb-2">
                        <span className="font-medium">Location:</span>{' '}
                        {selectedUser.location || 'Not specified'}
                      </p>
                    </div>
                  </div>
                )}

                {selectedUser.userType === 'employer' && selectedUser.plans && selectedUser.plans.length > 0 && (
                  <div className="md:col-span-2">
                    <h5 className="text-sm font-medium text-gray-500 mb-2">Subscription Plan</h5>
                    <div className="bg-gray-50 p-4 rounded-md">
                      <p className="mb-2">
                        <span className="font-medium">Current Plan:</span>{' '}
                        <span className="capitalize">{selectedUser.plans[0].planName}</span>
                      </p>
                      <p className="mb-2">
                        <span className="font-medium">Start Date:</span>{' '}
                        {formatDate(selectedUser.plans[0].startDate)}
                      </p>
                      <p className="mb-2">
                        <span className="font-medium">End Date:</span>{' '}
                        {selectedUser.plans[0].endDate ? formatDate(selectedUser.plans[0].endDate) : 'Ongoing'}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => handleToggleStatus(selectedUser)}
                  disabled={statusUpdateLoading}
                  className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white ${
                    selectedUser.isActive
                      ? 'bg-red-600 hover:bg-red-700'
                      : 'bg-green-600 hover:bg-green-700'
                  } focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                    selectedUser.isActive ? 'focus:ring-red-500' : 'focus:ring-green-500'
                  }`}
                >
                  {statusUpdateLoading ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                  ) : selectedUser.isActive ? (
                    <ToggleLeft size={16} className="mr-2" />
                  ) : (
                    <ToggleRight size={16} className="mr-2" />
                  )}
                  {selectedUser.isActive ? 'Deactivate User' : 'Activate User'}
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

// User Table Component
const UserTable = ({ users, onSelectUser, onToggleStatus, statusUpdateLoading, formatDate }) => {
  if (users.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        No users found. Try adjusting your search criteria.
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Registered</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {users.map((user) => (
            <tr key={user.id} className="hover:bg-gray-50">
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="flex items-center">
                  <div className="flex-shrink-0 h-10 w-10 flex items-center justify-center bg-gray-100 rounded-full">
                    {user.userType === 'candidate' ? (
                      <User size={20} className="text-blue-500" />
                    ) : (
                      <Briefcase size={20} className="text-green-500" />
                    )}
                  </div>
                  <div className="ml-4">
                    <div className="text-sm font-medium text-gray-900">{user.name}</div>
                    {user.userType === 'employer' && (
                      <div className="text-sm text-gray-500">{user.companyName}</div>
                    )}
                  </div>
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{user.email}</td>
              <td className="px-6 py-4 whitespace-nowrap">
                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                  user.userType === 'candidate' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'
                }`}>
                  {user.userType}
                </span>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                  user.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                }`}>
                  {user.isActive ? 'Active' : 'Inactive'}
                </span>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatDate(user.createdAt)}</td>
              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                <div className="flex space-x-2">
                  <button
                    onClick={() => onSelectUser(user)}
                    className="text-blue-600 hover:text-blue-900"
                  >
                    <Eye size={18} />
                  </button>
                  <button
                    onClick={() => onToggleStatus(user)}
                    disabled={statusUpdateLoading}
                    className={`${user.isActive ? 'text-red-600 hover:text-red-900' : 'text-green-600 hover:text-green-900'} ${
                      statusUpdateLoading ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                  >
                    {user.isActive ? <ToggleLeft size={18} /> : <ToggleRight size={18} />}
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default UserManagement;
